import type { Material } from "three";
import { Layers, Vector3, Color, Matrix4 } from "three";
import { SITE_CAMERA_FADE_LAYER } from "../../layerConstants";
import type { LayerSpecificUniformList } from "./LayerSpecificUniformList";
import { MaterialSharedParameterGroup } from "./MaterialSharedParameterGroup";
import type { SHLightProbeGrid } from "./SHLightProbeGrid";
import type { SHLightProbeGridData } from "./SHLightProbeGridData";
import { fillInterleavedCubeTexture } from "./SHLightProbeGridData";

export interface LayerSpecificUniforms {
  // Map from uniform names to values.
  uniforms: LayerSpecificUniformList;
  layers: Layers;
}

export interface LightOffsetMap extends LayerSpecificUniforms {
  appliedIntensityMultiplier: number;
  appliedDecayMultiplier: number;
}

export interface CameraFadeSettings {
  minCameraFadeDistance: number;
  maxCameraFadeDistance: number;
  layers: Layers;
}

const emptyLightProbeUniforms = {
  lightProbeData: null,
  lightProbeCount: new Int32Array([1, 1, 1]),
  lightProbeCubeResolution: 8,
  worldToLightProbeGrid: new Matrix4(),
};

const flatAmbientProbeUniforms = {
  lightProbeData: fillInterleavedCubeTexture(1, 1, 1, 4, [0.35, 0.35, 0.35]),
  lightProbeCount: new Int32Array([1, 1, 1]),
  lightProbeCubeResolution: 4,
  worldToLightProbeGrid: new Matrix4(),
};

const defaultFadeSettings = {
  minCameraFadeDistance: 0,
  maxCameraFadeDistance: 0.001,
};

const emptyFrostedGlassPMREMUniforms = {
  frostedGlassPmrem: null,
  frostedGlassPmremTexelWidth: 1,
  frostedGlassPmremTexelHeight: 1,
  frostedGlassPmremMaxMip: 0,
};

export function setLightProbeUniformsFromGridData(uniforms: LayerSpecificUniformList, probeData: SHLightProbeGridData) {
  uniforms["lightProbeData"] = probeData.interleavedCubeDataTexture ?? null;
  uniforms["lightProbeCubeResolution"] = probeData.cubeMapResolution;
  uniforms["lightProbeCount"] = new Int32Array([probeData.probeCountX, probeData.probeCountY, probeData.probeCountZ]);
  uniforms["worldToLightProbeGrid"] = probeData.worldToGridMatrix;
}

export class SSFXRendererLayerUniforms {
  private emptyLightOffsetMapUniforms: LayerSpecificUniformList;

  lightProbeGrids: SHLightProbeGrid[] = [];

  // Light probe grids. The light probe grid is selected from this list according to layers mask.
  // Grids in the beginning of the list have priority.
  globalIrradianceGrids: SHLightProbeGrid[] = [];
  frostedGlassGrids: SHLightProbeGrid[] = [];

  // These layer specific uniforms get applied the same way as light probe grids.
  lightOffsetMaps: LightOffsetMap[] = [];
  cameraFadeSettings: CameraFadeSettings[] = [];

  private currentGlobalIrradianceGrid: SHLightProbeGrid | undefined;
  private lightProbeUniformsInitialized = false;

  private currentLightOffsetMap: LayerSpecificUniforms | undefined;

  private currentFadeSettings: Omit<CameraFadeSettings, "layers"> | null = null;

  private currentFrostedGlassPMREM: LayerSpecificUniformList | undefined;

  lightProbeBouncesToUse: number = 1;
  useApproximateLightProbeData: boolean = false;
  substituteMissingProbesWithFlatAmbient: boolean = true;

  constructor() {
    this.emptyLightOffsetMapUniforms = {
      lightOffsetMapWorldSize: new Vector3(5, 5, 5),
      lightOffsetMapWorldOffset: new Vector3(0, 0, 0),
      lightOffsetMap: null,
      lightOffsetMapScale: new Vector3(5, 5, 5),
      lightOffsetMapGridSize: new Vector3(1, 1, 1),
      lightOffsetMapColor: new Color(0x000000),
      lightOffsetMapDecay: 1,
      lightOffsetMapDistance: 1,
    };

    const cameraFadeLayers = new Layers();
    cameraFadeLayers.set(SITE_CAMERA_FADE_LAYER);
    this.cameraFadeSettings.push({
      minCameraFadeDistance: 450,
      maxCameraFadeDistance: 600,
      layers: cameraFadeLayers,
    });

    this.simpleGetUniformsForLayerMask = this.simpleGetUniformsForLayerMaskInternal.bind(this);
    this.getUniformsForLayerMask = this.getUniformsForLayerMaskInternal.bind(this);
  }

  disposeLightOffsetMaps() {
    this.lightOffsetMaps.splice(0, this.lightOffsetMaps.length);
  }

  updateLightProbeGrids(newGrids: SHLightProbeGrid[], interruptAsyncProbeUpdate: () => void) {
    let changed = false;
    if (this.lightProbeGrids.length !== newGrids.length) {
      changed = true;
    } else {
      for (let i = 0, l = this.lightProbeGrids.length; i < l; ++i) {
        if (newGrids[i] !== this.lightProbeGrids[i]) {
          changed = true;
          break;
        }
      }
    }
    if (changed) {
      interruptAsyncProbeUpdate();
      this.lightProbeGrids = [...newGrids];

      this.globalIrradianceGrids = newGrids.filter((grid) => grid.useForGlobalIrradiance);
      this.frostedGlassGrids = newGrids.filter((grid) => grid.lightProbes[0]?.willGeneratePmrem());
    }
  }

  readonly simpleGetUniformsForLayerMask: (
    layers: Layers,
    _material: Material,
    forceRefresh: boolean,
  ) => LayerSpecificUniformList;
  readonly getUniformsForLayerMask: (
    layers: Layers,
    _material: Material,
    forceRefresh: boolean,
  ) => LayerSpecificUniformList;

  // This version of getUniformsForLayerMask can be used when SSFXRenderer is not used.
  private simpleGetUniformsForLayerMaskInternal(
    layers: Layers,
    _material: Material,
    forceRefresh: boolean,
  ): LayerSpecificUniformList {
    const uniforms: LayerSpecificUniformList = {};

    let chosenFadeSettings: Omit<CameraFadeSettings, "layers"> = defaultFadeSettings;
    for (const fadeSettings of this.cameraFadeSettings) {
      if (layers.test(fadeSettings.layers)) {
        chosenFadeSettings = fadeSettings;
        break;
      }
    }
    let chosenLightMap: LayerSpecificUniforms | undefined;
    for (const lightMap of this.lightOffsetMaps) {
      if (layers.test(lightMap.layers)) {
        chosenLightMap = lightMap;
        break;
      }
    }

    if (this.currentFadeSettings !== chosenFadeSettings || forceRefresh) {
      this.currentFadeSettings = chosenFadeSettings;
      uniforms.invCameraFadeRange =
        1.0 / Math.max(0.001, chosenFadeSettings.maxCameraFadeDistance - chosenFadeSettings.minCameraFadeDistance);
      uniforms.minCameraFadeDistance = chosenFadeSettings.minCameraFadeDistance;
    }
    if (this.currentLightOffsetMap !== chosenLightMap || forceRefresh) {
      this.currentLightOffsetMap = chosenLightMap;
      if (this.currentLightOffsetMap) {
        Object.assign(uniforms, this.currentLightOffsetMap.uniforms);
      } else {
        // Return uniforms that don't have any light map data.
        Object.assign(uniforms, this.emptyLightOffsetMapUniforms);
      }
    }

    // ALWAYS include highlightedIds to ensure it's set via the same texture unit allocation path.
    uniforms.highlightedIds = MaterialSharedParameterGroup.instance.getHighlightTexture();

    return uniforms;
  }

  private getUniformsForLayerMaskInternal(
    layers: Layers,
    _material: Material,
    forceRefresh: boolean,
  ): LayerSpecificUniformList {
    let chosenGlobalIrradianceGrid: SHLightProbeGrid | undefined;
    for (const grid of this.globalIrradianceGrids) {
      if (layers.test(grid.layers)) {
        chosenGlobalIrradianceGrid = grid;
        break;
      }
    }
    let chosenLightMap: LayerSpecificUniforms | undefined;
    for (const lightMap of this.lightOffsetMaps) {
      if (layers.test(lightMap.layers)) {
        chosenLightMap = lightMap;
        break;
      }
    }
    let chosenFadeSettings: Omit<CameraFadeSettings, "layers"> = defaultFadeSettings;
    for (const fadeSettings of this.cameraFadeSettings) {
      if (layers.test(fadeSettings.layers)) {
        chosenFadeSettings = fadeSettings;
        break;
      }
    }
    let chosenFrostedGlassPMREM: LayerSpecificUniformList | undefined;
    for (const frostedGlassGrid of this.frostedGlassGrids) {
      if (layers.test(frostedGlassGrid.layers)) {
        chosenFrostedGlassPMREM = frostedGlassGrid.lightProbes[0]?.getPmremUniforms();
        break;
      }
    }

    const uniforms: LayerSpecificUniformList = {};

    if (this.currentFadeSettings !== chosenFadeSettings || forceRefresh) {
      this.currentFadeSettings = chosenFadeSettings;
      uniforms.invCameraFadeRange =
        1.0 / Math.max(0.001, chosenFadeSettings.maxCameraFadeDistance - chosenFadeSettings.minCameraFadeDistance);
      uniforms.minCameraFadeDistance = chosenFadeSettings.minCameraFadeDistance;
    }

    // We need to ensure that texture units are always assigned in the same order.
    // For that reason we always refresh lightmap uniforms in case light probe grid uniforms are refreshed.
    // Also refresh if uniforms have never been initialized (both undefined on first call).
    const refreshLightProbeGridUniforms =
      this.currentGlobalIrradianceGrid !== chosenGlobalIrradianceGrid ||
      forceRefresh ||
      !this.lightProbeUniformsInitialized;

    if (this.currentLightOffsetMap !== chosenLightMap || refreshLightProbeGridUniforms || forceRefresh) {
      this.currentLightOffsetMap = chosenLightMap;
      if (this.currentLightOffsetMap) {
        Object.assign(uniforms, this.currentLightOffsetMap.uniforms);
      } else {
        // Return uniforms that don't have any light map data.
        Object.assign(uniforms, this.emptyLightOffsetMapUniforms);
      }
    }

    // ALWAYS send light probe uniforms to ensure Three.js texture unit caching doesn't cause stale bindings.
    // Three.js r182+ caches texture units and may skip gl.uniform1i if it thinks the unit hasn't changed,
    // but texture bindings can be overwritten by other passes (depth, shadow, etc.).
    this.currentGlobalIrradianceGrid = chosenGlobalIrradianceGrid;
    this.lightProbeUniformsInitialized = true;
    if (this.currentGlobalIrradianceGrid) {
      const probeData = this.currentGlobalIrradianceGrid.getDataForRendering(
        this.lightProbeBouncesToUse,
        this.useApproximateLightProbeData,
      );
      if (probeData !== null) {
        setLightProbeUniformsFromGridData(uniforms, probeData);
      } else if (this.substituteMissingProbesWithFlatAmbient) {
        Object.assign(uniforms, flatAmbientProbeUniforms);
      } else {
        Object.assign(uniforms, emptyLightProbeUniforms);
      }
    } else {
      // No grid selected - keep probe data empty.
      Object.assign(uniforms, emptyLightProbeUniforms);
    }

    // ALWAYS send frosted glass PMREM uniforms to ensure Three.js texture unit caching doesn't cause stale bindings.
    this.currentFrostedGlassPMREM = chosenFrostedGlassPMREM;
    if (this.currentFrostedGlassPMREM) {
      Object.assign(uniforms, this.currentFrostedGlassPMREM);
    } else {
      Object.assign(uniforms, emptyFrostedGlassPMREMUniforms);
    }

    // ALWAYS include highlightedIds in layer-specific uniforms to ensure it's set via the same
    // texture unit allocation path as lightProbeData. This prevents texture slot conflicts.
    uniforms.highlightedIds = MaterialSharedParameterGroup.instance.getHighlightTexture();

    return uniforms;
  }
}
